import { createContext, useContext, useCallback, type ReactNode } from 'react'
import { useWorkspace } from './WorkspaceContext'
import { useAppSelector, useAppDispatch } from '../store/hooks'
import {
    createListSaga,
    updateListSaga,
    deleteListSaga,
    transferListSaga
} from '../store/lists/listsSlice'
import {
    selectAllLists,
    selectListsLoading,
    selectListsError
} from '../store/lists/listsSelectors'
import { useMemo } from 'react'

export interface List {
    id: string
    projectId: string
    workspaceId: string
    name: string
    order: number
    createdAt: any
}

interface ListsContextValue {
    lists: List[]
    loading: boolean
    error: string | null
    createList: (projectId: string, name: string, afterListId?: string) => Promise<string | null>
    updateList: (listId: string, data: Partial<Pick<List, 'name' | 'order'>>) => Promise<void>
    deleteList: (listId: string) => Promise<void>
    transferList: (listId: string, newProjectId: string) => Promise<void>
}

const ListsContext = createContext<ListsContextValue | null>(null)

export const useLists = () => {
    const context = useContext(ListsContext)
    if (!context) {
        throw new Error('useLists must be used within ListsProvider')
    }
    return context
}

interface ListsProviderProps {
    children: ReactNode
    projectId?: string
}

export const ListsProvider = ({ children, projectId }: ListsProviderProps) => {
    const dispatch = useAppDispatch()
    const { workspace } = useWorkspace()
    const workspaceId = workspace?.id
    const allLists = useAppSelector(selectAllLists)
    const lists = useMemo(
        () => projectId ? allLists.filter((l: List) => l.projectId === projectId) : allLists,
        [allLists, projectId]
    )
    const loading = useAppSelector(selectListsLoading)
    const error = useAppSelector(selectListsError)

    const createList = useCallback(async (projectId: string, name: string, afterListId?: string): Promise<string | null> => {
        if (!workspaceId) {
            console.error('Cannot create list: missing workspaceId')
            return null
        }
        dispatch(createListSaga({ projectId, name, workspaceId, afterListId }))
        return null
    }, [dispatch, workspaceId])

    const updateList = useCallback(async (listId: string, data: Partial<Pick<List, 'name' | 'order'>>): Promise<void> => {
        dispatch(updateListSaga({ listId, data }))
    }, [dispatch])

    const deleteList = useCallback(async (listId: string): Promise<void> => {
        dispatch(deleteListSaga(listId))
    }, [dispatch])

    const transferList = useCallback(async (listId: string, newProjectId: string): Promise<void> => {
        dispatch(transferListSaga({ listId, newProjectId }))
    }, [dispatch])

    const value: ListsContextValue = {
        lists,
        loading,
        error,
        createList,
        updateList,
        deleteList,
        transferList
    }

    return (
        <ListsContext.Provider value={value}>
            {children}
        </ListsContext.Provider>
    )
}
